import React, { useState, useRef, useEffect } from 'react';
import { FaPlay, FaPause, FaSpinner, FaBook, FaUser, FaUsers, FaStar, FaChevronRight, FaCog, FaGraduationCap, FaCheckCircle, FaVolumeUp, FaRaspberryPi, FaRockrms } from 'react-icons/fa';

const AIAvatarTutor = () => {
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [customTopic, setCustomTopic] = useState('');
  const [selectedProfessor, setSelectedProfessor] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [videoGenerated, setVideoGenerated] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  const speechRef = useRef(null);
  const animationFrameRef = useRef(null);

  const subjects = {
    geology: {
      name: 'Geology',
      icon: '🌍',
      topics: ['Structural Geology', 'Mineralogy', 'Economic Geology', 'Petrology', 'Sedimentology']
    },
    engineering: {
      name: 'Engineering',
      icon: '⚙️',
      topics: ['Signal Processing', 'Control Systems', 'Thermodynamics', 'Fluid Mechanics']
    },
    programming: {
      name: 'Programming',
      icon: '💻',
      topics: ['JavaScript Fundamentals', 'Python Data Structures', 'Rust Ownership', 'Java OOP']
    },
    jee: {
      name: 'JEE (Physics)',
      icon: '⚛️',
      topics: ['Mechanics', 'Electromagnetism', 'Thermodynamics', 'Modern Physics', 'Optics']
    },
    neet: {
      name: 'NEET (Biology)',
      icon: '🧬',
      topics: ['Cell Biology', 'Genetics', 'Human Physiology', 'Ecology', 'Plant Physiology']
    },
    chemistry: {
      name: 'Chemistry',
      icon: '🧪',
      topics: ['Organic Chemistry', 'Physical Chemistry', 'Inorganic Chemistry', 'Analytical Chemistry']
    }
  };

  const professors = [
    {
      id: 'male1',
      name: 'Dr. Rajesh Kumar',
      gender: 'Male',
      specialty: 'Science & Engineering',
      voice: 'male',
      avatarColor: '#4A90E2'
    },
    {
      id: 'female1',
      name: 'Dr. Priya Sharma',
      gender: 'Female',
      specialty: 'Medical & Life Sciences',
      voice: 'female',
      avatarColor: '#E91E63'
    },
    {
      id: 'male2',
      name: 'Prof. Arjun Mehta',
      gender: 'Male',
      specialty: 'Mathematics & Programming',
      voice: 'male',
      avatarColor: '#10B981'
    },
    {
      id: 'female2',
      name: 'Dr. Ananya Singh',
      gender: 'Female',
      specialty: 'Humanities & Languages',
      voice: 'female',
      avatarColor: '#F59E0B'
    }
  ];

  const topicContent = {
    'Structural Geology': 'Structural geology is the study of the three-dimensional distribution of rock units with respect to their deformational histories. It examines how rocks deform and the processes behind mountain building, faulting, and folding. Key concepts include stress and strain, rock mechanics, and tectonic processes. Understanding structural geology is crucial for mineral exploration, petroleum geology, and assessing geological hazards.',
    'Mineralogy': 'Mineralogy is the scientific study of minerals, their crystallography, properties, and occurrences. Minerals are naturally occurring inorganic solids with a definite chemical composition and ordered atomic structure. We study their physical properties like hardness, cleavage, luster, and color. This knowledge is essential for identifying minerals in the field and understanding their formation processes.',
    'Signal Processing': 'Signal processing involves analyzing, modifying, and synthesizing signals such as sound, images, and scientific measurements. Digital signal processing uses mathematical algorithms to process discrete-time signals. Key topics include Fourier transforms, filters, sampling theory, and convolution. Applications range from audio processing to telecommunications and medical imaging.',
    'JavaScript Fundamentals': 'JavaScript is a versatile programming language primarily used for web development. Fundamentals include variables, data types, functions, objects, and arrays. Understanding scope, closures, and asynchronous programming with promises and async-await is crucial. JavaScript enables interactive web pages and is essential for modern web development frameworks like React, Vue, and Angular.',
    'Python Data Structures': 'Python provides several built-in data structures including lists, tuples, dictionaries, and sets. Lists are mutable sequences, tuples are immutable, dictionaries store key-value pairs, and sets contain unique elements. Understanding when to use each structure and their time complexities for operations is fundamental for efficient programming and algorithm design.',
    'Mechanics': 'Mechanics is the branch of physics dealing with motion and forces. Classical mechanics covers Newton\'s laws, energy conservation, momentum, and rotational motion. Key concepts include kinematics, dynamics, work-energy theorem, and simple harmonic motion. Mastering mechanics is essential for JEE Main and Advanced, as it forms the foundation for other physics topics.',
    'Cell Biology': 'Cell biology studies the structure and function of cells, the basic units of life. Topics include cell membranes, organelles like mitochondria and chloroplasts, cellular metabolism, and cell division through mitosis and meiosis. Understanding cellular processes is crucial for NEET preparation and forms the foundation for genetics, physiology, and molecular biology.',
    'Organic Chemistry': 'Organic chemistry is the study of carbon-containing compounds, their structures, properties, and reactions. Key topics include functional groups, reaction mechanisms, stereochemistry, and synthesis. Understanding concepts like resonance, inductive effects, and hyperconjugation helps predict reaction outcomes. Organic chemistry is vital for both JEE and NEET examinations.'
  };

  const generateContent = (topic) => {
    const predefined = topicContent[topic];
    if (predefined) return predefined;
    
    if (customTopic) {
      return `Let me explain ${customTopic} in detail. This topic is an important concept in ${subjects[selectedSubject]?.name}. Understanding this concept requires analyzing its fundamental principles, applications, and real-world relevance. We'll break down the key components and explore how they interconnect to form a comprehensive understanding. This knowledge is particularly valuable for competitive exam preparation and practical applications in the field.`;
    }
    
    return `Today we'll explore ${topic}, an essential topic in ${subjects[selectedSubject]?.name}. This subject matter is crucial for understanding advanced concepts and excelling in competitive examinations. We'll examine the theoretical foundations, practical applications, and problem-solving techniques associated with this topic.`;
  };

  const simulateVideoGeneration = () => {
    setIsGenerating(true);
    setGenerationProgress(0);
    setVideoGenerated(false);

    const interval = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsGenerating(false);
          setVideoGenerated(true);
          const content = generateContent(selectedTopic || customTopic);
          setDuration(Math.ceil(content.split(' ').length * 0.4));
          return 100;
        }
        return prev + 2;
      });
    }, 50);
  };

  const handleGenerate = () => {
    if (!selectedProfessor || (!selectedTopic && !customTopic)) {
      alert('Please select a professor and topic');
      return;
    }
    simulateVideoGeneration();
  };

  const resetSelection = () => {
    setVideoGenerated(false);
    setGenerationProgress(0);
    setIsPlaying(false);
    setCurrentTime(0);
    if (speechRef.current) {
      window.speechSynthesis.cancel();
    }
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      const selectedProf = professors.find(p => p.id === selectedProfessor);
      
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(v => 
        selectedProf?.voice === 'female' 
          ? v.name.includes('Female') || v.name.includes('female') 
          : v.name.includes('Male') || v.name.includes('male')
      );
      
      if (preferredVoice) utterance.voice = preferredVoice;
      utterance.rate = 0.9;
      utterance.pitch = selectedProf?.voice === 'female' ? 1.1 : 0.9;
      
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => {
        setIsSpeaking(false);
        setIsPlaying(false);
        setCurrentTime(0);
      };
      
      speechRef.current = utterance;
      window.speechSynthesis.speak(utterance);
      
      const startTime = Date.now();
      const animate = () => {
        if (isSpeaking) {
          const elapsed = (Date.now() - startTime) / 1000;
          setCurrentTime(elapsed);
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      };
      animate();
    }
  };

  const togglePlayPause = () => {
    if (!isPlaying) {
      const content = generateContent(selectedTopic || customTopic);
      speakText(content);
      setIsPlaying(true);
    } else {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      setIsSpeaking(false);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
    }
    
    return () => {
      if (speechRef.current) {
        window.speechSynthesis.cancel();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const selectedProf = professors.find(p => p.id === selectedProfessor);

  return (
    <>
      <link 
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" 
        rel="stylesheet"
      />
      
      <style>{`
        body {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          min-height: 100vh;
          color: #fff;
        }
        
        .gradient-bg {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        
        .glass-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
        }
        
        .subject-card {
          cursor: pointer;
          transition: all 0.3s ease;
          background: rgba(255, 255, 255, 0.05);
          border: 2px solid transparent;
          border-radius: 15px;
        }
        
        .subject-card:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: translateY(-5px);
        }
        
        .subject-card.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-color: #fff;
          box-shadow: 0 10px 30px rgba(102, 126, 234, 0.5);
        }
        
        .topic-btn {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #fff;
          transition: all 0.3s ease;
          border-radius: 10px;
        }
        
        .topic-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          color: #fff;
        }
        
        .topic-btn.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-color: #764ba2;
        }
        
        .professor-card {
          cursor: pointer;
          transition: all 0.3s ease;
          background: rgba(255, 255, 255, 0.05);
          border: 2px solid transparent;
          border-radius: 15px;
        }
        
        .professor-card:hover {
          transform: translateY(-5px);
          background: rgba(255, 255, 255, 0.1);
        }
        
        .professor-card.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-color: #fff;
          box-shadow: 0 10px 30px rgba(102, 126, 234, 0.5);
        }
        
        .btn-generate {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          padding: 15px 30px;
          font-size: 1.1rem;
          font-weight: bold;
          border-radius: 10px;
          box-shadow: 0 10px 30px rgba(102, 126, 234, 0.5);
          transition: all 0.3s ease;
        }
        
        .btn-generate:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(102, 126, 234, 0.7);
        }
        
        .btn-generate:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .progress-bar-animated {
          background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #667eea 100%);
          background-size: 200% 100%;
          animation: shimmer 2s infinite;
        }
        
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        
        .video-container {
          background: #000;
          border-radius: 15px;
          overflow: hidden;
          position: relative;
          aspect-ratio: 16/9;
        }
        
        .avatar-display {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          position: relative;
        }
        
        .avatar-circle {
          width: 200px;
          height: 200px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 100px;
          animation: ${props => props.speaking ? 'pulse 1s infinite' : 'none'};
          transition: all 0.3s ease;
        }
        
        .avatar-circle.speaking {
          animation: pulse 1s infinite;
          box-shadow: 0 0 40px rgba(102, 126, 234, 0.6);
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        .classroom-bg {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 40%;
          background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
          display: flex;
          align-items: flex-end;
          justify-content: center;
          padding: 20px;
        }
        
        .info-box {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
          padding: 15px;
        }
        
        .header-gradient {
          background: rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .text-gradient {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        textarea {
          background: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          color: #fff !important;
          border-radius: 10px !important;
        }
        
        textarea:focus {
          background: rgba(255, 255, 255, 0.08) !important;
          border-color: #667eea !important;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25) !important;
          color: #fff !important;
        }
        
        textarea::placeholder {
          color: rgba(255, 255, 255, 0.4) !important;
        }
        
        .video-controls {
          background: rgba(0, 0, 0, 0.8);
          padding: 15px;
          border-radius: 10px;
        }
        
        .play-btn {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .play-btn:hover {
          transform: scale(1.1);
          box-shadow: 0 5px 20px rgba(102, 126, 234, 0.6);
        }
        
        .timeline {
          height: 6px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
          position: relative;
          cursor: pointer;
        }
        
        .timeline-progress {
          height: 100%;
          background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
          border-radius: 3px;
          transition: width 0.1s linear;
        }
      `}</style>

      <div style={{ minHeight: '100vh', paddingBottom: '50px' }}>
        {/* Header */}
        <div className="header-gradient py-5 my-5 mb-4">
          <div className="container">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                <FaRockrms className="text-warning" size={32} />
                <h1 className="mb-0 h3 fw-bold text-gradient">EdVision Studio</h1>
              </div>
              <div className="badge bg-primary fs-6">AI Avatar Tutor System</div>
            </div>
          </div>
        </div>

        <div className="container">
          {!videoGenerated ? (
            <div className="row g-4">
              {/* Left Panel - Configuration */}
              <div className="col-lg-6">
                {/* Subject Selection */}
                <div className="glass-card p-4 mb-4">
                  <h2 className="h5 mb-3 d-flex align-items-center gap-2">
                    <FaBook className="text-primary" />
                    Select Subject
                  </h2>
                  <div className="row g-3">
                    {Object.entries(subjects).map(([key, subject]) => (
                      <div key={key} className="col-6">
                        <div
                          onClick={() => {
                            setSelectedSubject(key);
                            setSelectedTopic('');
                            setCustomTopic('');
                          }}
                          className={`subject-card p-3 text-center ${selectedSubject === key ? 'active' : ''}`}
                        >
                          <div style={{ fontSize: '2.5rem' }} className="mb-2">{subject.icon}</div>
                          <div className="fw-semibold">{subject.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Topic Selection */}
                {selectedSubject && (
                  <div className="glass-card p-4 mb-4">
                    <h2 className="h5 mb-3">Select Topic</h2>
                    <div className="d-grid gap-2 mb-3">
                      {subjects[selectedSubject].topics.map((topic) => (
                        <button
                          key={topic}
                          onClick={() => {
                            setSelectedTopic(topic);
                            setCustomTopic('');
                          }}
                          className={`btn topic-btn text-start d-flex align-items-center justify-content-between ${
                            selectedTopic === topic ? 'active' : ''
                          }`}
                        >
                          <span>{topic}</span>
                          <FaChevronRight size={14} />
                        </button>
                      ))}
                    </div>
                    
                    <div className="pt-3 border-top border-secondary">
                      <label className="form-label text-primary small">Or enter custom topic:</label>
                      <textarea
                        value={customTopic}
                        onChange={(e) => {
                          setCustomTopic(e.target.value);
                          setSelectedTopic('');
                        }}
                        placeholder="Enter your specific topic or concept to explain..."
                        className="form-control"
                        rows="4"
                      />
                    </div>
                  </div>
                )}

                {/* Professor Selection */}
                <div className="glass-card p-4 mb-4">
                  <h2 className="h5 mb-3 d-flex align-items-center gap-2">
                    <FaUsers className="text-primary" />
                    Select Professor
                  </h2>
                  <div className="row g-3">
                    {professors.map((prof) => (
                      <div key={prof.id} className="col-6">
                        <div
                          onClick={() => setSelectedProfessor(prof.id)}
                          className={`professor-card p-3 ${selectedProfessor === prof.id ? 'active' : ''}`}
                        >
                          <div 
                            style={{ 
                              background: prof.avatarColor,
                              height: '100px',
                              borderRadius: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '3rem',
                              marginBottom: '10px'
                            }}
                          >
                            {prof.gender === 'Male' ? '👨‍🏫' : '👩‍🏫'}
                          </div>
                          <div className="fw-semibold small">{prof.name}</div>
                          <div className="text-primary" style={{ fontSize: '0.75rem' }}>{prof.specialty}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="btn btn-generate text-white w-100 d-flex align-items-center justify-content-center gap-2"
                >
                  {isGenerating ? (
                    <>
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Generating... {generationProgress}%
                    </>
                  ) : (
                    <>
                      <FaPlay />
                      Generate AI Explanation
                    </>
                  )}
                </button>
              </div>

              {/* Right Panel - Preview/Info */}
              <div className="col-lg-6">
                <div className="glass-card p-4" style={{ minHeight: '400px' }}>
                  <h2 className="h5 mb-4">Preview & Information</h2>
                  
                  {isGenerating && (
                    <div>
                      <div className="info-box mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-primary small">Generation Progress</span>
                          <span className="fw-bold">{generationProgress}%</span>
                        </div>
                        <div className="progress" style={{ height: '10px' }}>
                          <div 
                            className="progress-bar progress-bar-animated"
                            role="progressbar"
                            style={{ width: `${generationProgress}%` }}
                            aria-valuenow={generationProgress}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          />
                        </div>
                      </div>
                      
                      <div className="small text-primary">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Analyzing topic content...
                        </div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Generating script with AI...
                        </div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Preparing avatar...
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Setting up voice synthesis...
                        </div>
                      </div>
                    </div>
                  )}

                  {!isGenerating && !selectedSubject && (
                    <div className="text-center py-5">
                      <FaGraduationCap size={64} className="text-primary mb-3" />
                      <h3 className="h5 mb-2">Welcome to EdVision Studio</h3>
                      <p className="text-muted small">
                        Select a subject, topic, and professor to generate your AI-powered video explanation
                      </p>
                    </div>
                  )}

                  {!isGenerating && selectedSubject && (
                    <div>
                      <div className="alert alert-primary" role="alert">
                        <h3 className="h6 mb-2">Current Selection:</h3>
                        <div className="small">
                          <div className="mb-1"><strong>Subject:</strong> {subjects[selectedSubject].name}</div>
                          <div className="mb-1"><strong>Topic:</strong> {selectedTopic || customTopic || 'Not selected'}</div>
                          <div><strong>Professor:</strong> {professors.find(p => p.id === selectedProfessor)?.name || 'Not selected'}</div>
                        </div>
                      </div>

                      <div className="info-box">
                        <h3 className="h6 mb-2 small">What to expect:</h3>
                        <ul className="small text-primary mb-0" style={{ paddingLeft: '20px' }}>
                          <li className="mb-1">✓ Comprehensive topic explanation</li>
                          <li className="mb-1">✓ Animated avatar with voice</li>
                          <li className="mb-1">✓ Professional presentation</li>
                          <li className="mb-1">✓ Clear, engaging delivery</li>
                          <li>✓ Interactive playback controls</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Video Player View */
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div className="glass-card p-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="h4 mb-0">Generated Explanation</h2>
                    <button
                      onClick={resetSelection}
                      className="btn btn-outline-light"
                    >
                      Create New
                    </button>
                  </div>

                  {/* Video Player */}
                  <div className="video-container mb-4 d-flex align-items-center justify-content-center position-relative">
                    <div className="video-overlay position-absolute w-100 h-100" />
                    <div className="text-center position-relative" style={{ zIndex: 1 }}>
                      <FaPlay size={64} className="text-primary mb-3" />
                      <p className="h5 mb-2">AI Video Generated Successfully</p>
                      <p className="text-muted">Click play to start the explanation</p>
                    </div>
                  </div>

                  {/* Video Info */}
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="info-box">
                        <div className="text-primary small mb-1">Topic</div>
                        <div className="fw-semibold">{selectedTopic || customTopic}</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="info-box">
                        <div className="text-primary small mb-1">Professor</div>
                        <div className="fw-semibold">{professors.find(p => p.id === selectedProfessor)?.name}</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="info-box">
                        <div className="text-primary small mb-1">Duration</div>
                        <div className="fw-semibold">4:32 min</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Implementation Note */}
                <div className="alert-custom p-3 mt-4">
                  <h3 className="h6 mb-2">⚠️ Demo Note:</h3>
                  <p className="small mb-0">
                    This is a UI prototype. To make this functional, you need to integrate actual AI services 
                    (D-ID/HeyGen for avatars, ElevenLabs for voice, OpenAI for content generation). 
                    See the implementation guide in the response for complete backend setup.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AIAvatarTutor;